import { useCallback, useEffect, useMemo, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import Button, { ButtonSize, ButtonType, ButtonVariant } from 'src/components/atoms/Button';
import { Checkbox } from 'src/components/atoms/Checkbox';
import Gap from 'src/components/atoms/Gap';
import Text, { TextAlign, TextVariant } from 'src/components/atoms/Text';
import {
  AccountFields,
  accountTypeLabel,
  formatBalance,
  logStatementRejectionChanged,
  logStatementStaleStartOverridden,
  MAX_ACCOUNTS,
  MAX_STATEMENTS_PER_ACCOUNT,
  PanelAccount,
  StatementFields,
  useAssetVerificationReview
} from 'src/features/assetVerification';
import AccountSection from './AccountSection';
import { PanelWrapper, SectionHeading } from './styles';

// Last 4 is optional now, so it is out of the identity: two blank-last-4 accounts
// at the same holder/institution/type collapse to one reuse candidate, and the
// analyst splits them with the existing untick.
const IDENTITY_FIELDS = ['institutionName', 'accountType', 'accountHolderName'] as const;

const candidateLabel = (account: PanelAccount): string => {
  const base = `${account.accountHolderName}, ${account.institutionName} | ${accountTypeLabel(
    account.accountType
  )}`;
  return account.accountNumberLastFour ? `${base} - ****${account.accountNumberLastFour}` : base;
};

const REUSE_UNAVAILABLE_TEXT_ID = 'reuse-accounts-unavailable-reason';

const hasBlankIdentity = (account: PanelAccount) =>
  IDENTITY_FIELDS.every((field) => account[field] === '');

// Merge discards the target account and keeps only its statements, so a typed
// last 4 (not part of the reuse identity) would be silently lost. An account with
// any last 4 typed therefore isn't a merge target; the boxes disable instead.
const isBlankMergeTarget = (account: PanelAccount) =>
  hasBlankIdentity(account) && account.accountNumberLastFour === '';

type PanelFormValues = Record<string, AccountFields | StatementFields>;

const buildDefaultValues = (accounts: PanelAccount[]): PanelFormValues => {
  const values: PanelFormValues = {};
  accounts.forEach((account, ai) => {
    values[`acct${ai}`] = {
      institutionName: account.institutionName,
      accountType: account.accountType,
      accountHolderName: account.accountHolderName,
      accountNumberLastFour: account.accountNumberLastFour
    };
    account.statements.forEach((statement, si) => {
      values[`stmt${ai}_${si}`] = {
        startDate: statement.startDate,
        startBalance: statement.startBalance,
        endDate: statement.endDate,
        endBalance: statement.endBalance
      };
    });
  });
  return values;
};

type AssetVerificationDocumentPanelProps = {
  entryId: string;
  documentId: string;
  documentIndex: number;
  docShortId?: string;
};

type PanelFormProps = AssetVerificationDocumentPanelProps & {
  // Per-document view: each account carries ONLY this document's statements here;
  // the review-global entity (all statements) lives in Context under the same key.
  documentAccounts: PanelAccount[];
};

const PanelForm = ({
  entryId,
  documentId,
  documentIndex,
  docShortId,
  documentAccounts
}: PanelFormProps) => {
  const { state, dispatch } = useAssetVerificationReview();

  // Accounts captured on an earlier document (design note 23:6846). Accounts are
  // review-global entities now, so there is nothing to dedup — and one appearing on
  // this document stays listed (checked) so the analyst can untick it.
  const reuseCandidates = useMemo(() => {
    if (documentIndex === 0) return [];
    const indexById = new Map(state.documents.map((doc) => [doc.documentId, doc.index]));
    return state.accounts
      .filter((account) => !hasBlankIdentity(account))
      .filter((account) =>
        account.statements.some((statement) => {
          const index = indexById.get(statement.documentId) ?? -1;
          return index >= 0 && index < documentIndex;
        })
      );
  }, [state.documents, state.accounts, documentIndex]);

  // With last 4 optional, two different accounts can render the same label (same
  // holder/institution/type, both blank). Suffix colliding labels with the document
  // numbers the account was captured on — matching the "Document N of M" header —
  // so the analyst can tell which checkbox is which.
  const reuseCandidateLabels = useMemo(() => {
    const indexById = new Map(state.documents.map((doc) => [doc.documentId, doc.index]));
    const counts = new Map<string, number>();
    reuseCandidates.forEach((candidate) => {
      const label = candidateLabel(candidate);
      counts.set(label, (counts.get(label) ?? 0) + 1);
    });
    const suffixed = reuseCandidates.map((candidate) => {
      const label = candidateLabel(candidate);
      if ((counts.get(label) ?? 0) < 2) return [candidate.key, label] as const;
      const docNumbers = [
        ...new Set(
          candidate.statements
            .map((statement) => indexById.get(statement.documentId))
            .filter((index): index is number => index !== undefined)
            .map((index) => index + 1)
        )
      ].sort((a, b) => a - b);
      const noun = docNumbers.length > 1 ? 'documents' : 'document';
      return [candidate.key, `${label} (${noun} ${docNumbers.join(', ')})`] as const;
    });
    // Ordinal fallback: two same-identity accounts extracted from the SAME document
    // still collide after the doc suffix, so number the survivors.
    const suffixedCounts = new Map<string, number>();
    suffixed.forEach(([, label]) => {
      suffixedCounts.set(label, (suffixedCounts.get(label) ?? 0) + 1);
    });
    const seen = new Map<string, number>();
    return new Map(
      suffixed.map(([key, label]) => {
        if ((suffixedCounts.get(label) ?? 0) < 2) return [key, label] as const;
        const ordinal = (seen.get(label) ?? 0) + 1;
        seen.set(label, ordinal);
        return [key, `${label} #${ordinal}`] as const;
      })
    );
  }, [reuseCandidates, state.documents]);

  const methods = useForm<PanelFormValues>({
    defaultValues: buildDefaultValues(documentAccounts),
    mode: 'onBlur'
  });

  const handleOnSave = useCallback(
    (data: PanelFormValues) => {
      documentAccounts.forEach((account, ai) => {
        const accountFields = data[`acct${ai}`] as AccountFields | undefined;
        if (accountFields) {
          dispatch({
            type: 'SAVE_ACCOUNT_FIELDS',
            payload: { accountKey: account.key, fields: accountFields }
          });
        }
        account.statements.forEach((statement, si) => {
          const statementFields = data[`stmt${ai}_${si}`] as StatementFields | undefined;
          if (statementFields) {
            dispatch({
              type: 'SAVE_STATEMENT_FIELDS',
              payload: {
                accountKey: account.key,
                statementKey: statement.key,
                // The typeable date picker reports a cleared or unparseable date as
                // null; panel state (and validateDocument) expect strings.
                fields: {
                  ...statementFields,
                  startDate: statementFields.startDate ?? '',
                  endDate: statementFields.endDate ?? ''
                }
              }
            });
          }
        });
      });
    },
    [documentAccounts, dispatch]
  );

  // Structural changes remount this form (key on PanelForm) and re-init defaultValues
  // from Context — so every handler below flushes the form's current values FIRST, or
  // any in-progress (un-blurred) edit elsewhere in the document would be discarded.
  const flushThen = useCallback(
    (action: () => void) => {
      handleOnSave(methods.getValues());
      action();
    },
    [handleOnSave, methods]
  );

  const onAddStatement = useCallback(
    (accountKey: string) =>
      flushThen(() => dispatch({ type: 'ADD_STATEMENT', payload: { accountKey, documentId } })),
    [dispatch, flushThen, documentId]
  );
  const onRemoveStatement = useCallback(
    (accountKey: string, statementKey: string) =>
      flushThen(() =>
        dispatch({ type: 'REMOVE_STATEMENT', payload: { accountKey, statementKey } })
      ),
    [dispatch, flushThen]
  );
  const onAddAccount = useCallback(
    () => flushThen(() => dispatch({ type: 'ADD_ACCOUNT', payload: { documentId } })),
    [dispatch, flushThen, documentId]
  );
  const onRemoveAccount = useCallback(
    (accountKey: string) =>
      flushThen(() => dispatch({ type: 'REMOVE_ACCOUNT', payload: { accountKey, documentId } })),
    [dispatch, flushThen, documentId]
  );
  const onResolveReasonCode = useCallback(
    (accountKey: string, statementKey: string, code: number) =>
      flushThen(() =>
        dispatch({
          type: 'CLEAR_STATEMENT_REASON_CODE',
          payload: { accountKey, statementKey, code }
        })
      ),
    [dispatch, flushThen]
  );
  const onOverrideStaleStart = useCallback(
    (accountKey: string, statementKey: string) =>
      flushThen(() => {
        logStatementStaleStartOverridden(
          { applicant_id: state.applicantId, entry_id: entryId },
          { document_id: documentId, statement_key: statementKey }
        );
        dispatch({ type: 'OVERRIDE_STALE_START_DATE', payload: { accountKey, statementKey } });
      }),
    [dispatch, flushThen, state.applicantId, entryId, documentId]
  );
  // One rejection-change event per settled selection, not per toggle: the same
  // 400ms debounce the field saves use, keyed per statement so edits on two
  // statements each get their final event. Pending events flush on unmount so
  // moving to the next document does not drop the last state.
  const rejectionLogTimersRef = useRef(
    new Map<string, { timer: ReturnType<typeof setTimeout>; log: () => void }>()
  );
  useEffect(() => {
    const timers = rejectionLogTimersRef.current;
    return () =>
      timers.forEach(({ timer, log }) => {
        clearTimeout(timer);
        log();
      });
  }, []);
  const onChangeAnalystReasonCodes = useCallback(
    (accountKey: string, statementKey: string, codes: number[]) =>
      flushThen(() => {
        const timers = rejectionLogTimersRef.current;
        const pending = timers.get(statementKey);
        if (pending) clearTimeout(pending.timer);
        const log = () => {
          timers.delete(statementKey);
          logStatementRejectionChanged(
            { applicant_id: state.applicantId, entry_id: entryId },
            {
              document_id: documentId,
              statement_key: statementKey,
              codes,
              code_count: codes.length
            }
          );
        };
        timers.set(statementKey, { timer: setTimeout(log, 400), log });
        dispatch({
          type: 'SET_STATEMENT_ANALYST_REASON_CODES',
          payload: { accountKey, statementKey, codes }
        });
      }),
    [dispatch, flushThen, state.applicantId, entryId, documentId]
  );

  // A reuse tick folds the document's blank account into the candidate entity; only
  // audit-only documents get a blank from buildPanelState. The tick needs exactly one
  // blank target: with none there is nothing to fill, and with two the merge would
  // claim whichever is topmost rather than the one the analyst means — either way the
  // unchecked boxes disable with the reason shown instead of silently doing nothing.
  const blankTargets = documentAccounts.filter(isBlankMergeTarget);
  const reuseUnavailableReason =
    blankTargets.length === 0
      ? "These accounts can't be reused here. To reuse one, clear every field on this document's account, including last 4."
      : blankTargets.length > 1
      ? 'More than one account on this document is blank, so a reused account has no single place to land. Fill in or remove the extra blank accounts first.'
      : null;

  const onToggleReuse = useCallback(
    (candidate: PanelAccount) => {
      const isOnDocument = candidate.statements.some(
        (statement) => statement.documentId === documentId
      );
      if (isOnDocument) {
        // Untick: detach this document's statements back into an unidentified
        // account — typed statement values survive, the identity link is undone.
        flushThen(() =>
          dispatch({
            type: 'SPLIT_ACCOUNT_STATEMENTS',
            payload: { accountKey: candidate.key, documentId }
          })
        );
        return;
      }
      const targets = documentAccounts.filter(isBlankMergeTarget);
      if (targets.length !== 1) return;
      const target = targets[0];
      // Tick: this document's blank account IS the candidate continuing here — merge
      // so the account is submitted once with all its statements (contract §5: the
      // grouping travels by identity; a per-document split inflated the total).
      flushThen(() =>
        dispatch({
          type: 'MERGE_ACCOUNT_STATEMENTS',
          payload: { targetAccountKey: target.key, sourceAccountKey: candidate.key }
        })
      );
    },
    [documentAccounts, dispatch, flushThen, documentId]
  );

  // Blur alone loses the edit in focus when the analyst refreshes mid-typing —
  // a debounced watch-save keeps the cache current without a write per keystroke.
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const subscription = methods.watch(() => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => handleOnSave(methods.getValues()), 400);
    });
    return () => {
      subscription.unsubscribe();
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [methods, handleOnSave]);

  // The wire caps statements per ACCOUNT (all documents), not per panel slice.
  const globalStatementCount = useCallback(
    (accountKey: string) =>
      state.accounts.find((account) => account.key === accountKey)?.statements.length ?? 0,
    [state.accounts]
  );

  return (
    <PanelWrapper data-testid="asset-verification-panel">
      {docShortId && (
        <>
          <Text isBold={true} align={TextAlign.center} variant={TextVariant.h3}>
            Doc ID: {docShortId}
          </Text>
          <Gap height={1.5} />
        </>
      )}
      {reuseCandidates.length > 0 && (
        <section data-testid="reuse-accounts">
          {/* Figma 23:6856: the reuse list carries its own "Account Information" heading. */}
          <SectionHeading>Account Information</SectionHeading>
          {reuseUnavailableReason !== null && (
            <>
              {/* The id lets each disabled checkbox point a screen reader at the reason. */}
              <div id={REUSE_UNAVAILABLE_TEXT_ID}>
                <Text align={TextAlign.left} testId="reuse-accounts-unavailable">
                  {reuseUnavailableReason}
                </Text>
              </div>
              <Gap height={0.5} />
            </>
          )}
          {reuseCandidates.map((candidate, candidateIndex) => {
            const isChecked = candidate.statements.some(
              (statement) => statement.documentId === documentId
            );
            return (
              <Checkbox
                key={candidate.key}
                name={`reuse-account-${candidateIndex}`}
                data-testid={`reuse-account-${candidateIndex}`}
                value={candidate.key}
                label={reuseCandidateLabels.get(candidate.key) ?? candidateLabel(candidate)}
                isChecked={isChecked}
                isDisabled={!isChecked && reuseUnavailableReason !== null}
                aria-describedby={
                  !isChecked && reuseUnavailableReason !== null
                    ? REUSE_UNAVAILABLE_TEXT_ID
                    : undefined
                }
                onClick={() => onToggleReuse(candidate)}
                showLabel={true}
              />
            );
          })}
          <Gap height={1.5} />
        </section>
      )}
      <FormProvider {...methods}>
        {/* Persistence is never validity-gated: an invalid form must still reach
            Context/sessionStorage on blur, or a reload loses the analyst's input.
            Submit-time validity is enforced separately by validateReviewForSubmit. */}
        <form
          onBlur={() => {
            // Balances are uncontrolled RHF inputs, so setting the formatted
            // string here rewrites the field the analyst just left.
            documentAccounts.forEach((account, ai) =>
              account.statements.forEach((_statement, si) => {
                (['startBalance', 'endBalance'] as const).forEach((field) => {
                  const name = `stmt${ai}_${si}.${field}` as const;
                  const raw = methods.getValues(name);
                  if (typeof raw === 'string' && raw !== '') {
                    methods.setValue(name, formatBalance(raw));
                  }
                });
              })
            );
            handleOnSave(methods.getValues());
          }}
        >
          {documentAccounts.map((account, accountIndex) => (
            <AccountSection
              key={account.key}
              account={account}
              accountIndex={accountIndex}
              canRemoveAccount={documentAccounts.length > 1}
              canAddStatement={globalStatementCount(account.key) < MAX_STATEMENTS_PER_ACCOUNT}
              onAddStatement={() => onAddStatement(account.key)}
              onRemoveStatement={(statementKey) => onRemoveStatement(account.key, statementKey)}
              onRemoveAccount={() => onRemoveAccount(account.key)}
              onResolveReasonCode={(statementKey, code) =>
                onResolveReasonCode(account.key, statementKey, code)
              }
              onChangeAnalystReasonCodes={(statementKey, codes) =>
                onChangeAnalystReasonCodes(account.key, statementKey, codes)
              }
              onOverrideStaleStart={(statementKey) =>
                onOverrideStaleStart(account.key, statementKey)
              }
            />
          ))}
        </form>
      </FormProvider>
      <Button
        name="add-account"
        data-testid="add-account"
        type={ButtonType.button}
        size={ButtonSize.normal}
        variant={ButtonVariant.outline}
        isDisabled={state.accounts.length >= MAX_ACCOUNTS}
        onClick={onAddAccount}
      >
        + Add another account
      </Button>
      <Gap height={1} />
    </PanelWrapper>
  );
};

const AssetVerificationDocumentPanel = ({
  entryId,
  documentId,
  documentIndex,
  docShortId
}: AssetVerificationDocumentPanelProps) => {
  const { state } = useAssetVerificationReview();

  // Per-document view over the review-global accounts: only accounts with at least
  // one statement on this document render here, and only those statements.
  const documentAccounts = useMemo(
    () =>
      state.accounts
        .map((account) => ({
          ...account,
          statements: account.statements.filter((statement) => statement.documentId === documentId)
        }))
        .filter((account) => account.statements.length > 0),
    [state.accounts, documentId]
  );

  // Account count + per-account statement counts: any structural change re-keys the
  // form so react-hook-form re-initialises defaultValues from Context.
  const structureSignature = documentAccounts
    .map((account) => `${account.key}:${account.statements.length}`)
    .join('|');

  return (
    <PanelForm
      key={structureSignature}
      entryId={entryId}
      documentId={documentId}
      documentIndex={documentIndex}
      docShortId={docShortId}
      documentAccounts={documentAccounts}
    />
  );
};

export default AssetVerificationDocumentPanel;
